import { selectProjectData } from "../../store/slices/projectSlice";
import { selectSendLists } from "../../store/slices/sendListsSlice";
import { store } from "../../store/store";

const usedIn = (arg) => {
  const projectData = selectProjectData(store.getState());
  const pSendlists = selectSendLists(store.getState());
  const handlers = [];
  const listeners = [];

  const getListenerCondition = (eventListener) =>
    eventListener?.ListenerCondition?.Condition ||
    eventListener?.ListenerCondition;

  const parseBusinessHoursValue = (value) => {
    if (!value || typeof value !== "string") return value;

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const businessHoursUsesArg = (businessHours) =>
    businessHours?.InputArgumentUUID === arg?.UUID ||
    businessHours?.ConditionInputArgumentUUID === arg?.UUID;

  projectData.BusinessHours?.forEach((businessHours) => {
    if (businessHoursUsesArg(businessHours)) {
      listeners.push({
        workflow: null,
        eventListener: null,
        eventHandler: null,
        businessHours,
      });
    }
  });

  projectData.InputArguments?.forEach((inputArgument) => {
    if (inputArgument.UUID === arg?.UUID || inputArgument.Format !== "business-hours") {
      return;
    }

    if (
      [inputArgument.Default, inputArgument.DefaultValue].some((defaultValue) =>
        businessHoursUsesArg(parseBusinessHoursValue(defaultValue))
      )
    ) {
      listeners.push({
        workflow: null,
        eventListener: null,
        eventHandler: null,
        inputArgument,
      });
    }
  });

  // find the sendLists where the arg is used
  const sendLists = [];
  pSendlists?.forEach((sl) => {
    if (sl.filters?.some((f) => f.valueId === arg?.UUID)) {
      sendLists.push(sl.uuid);
    }
    if (sl.filters?.some((f) => f.offset === arg?.UUID)) {
      sendLists.push(sl.uuid);
    }
    if (sl.filters?.some((f) => f.timeZone === arg?.UUID)) {
      sendLists.push(sl.uuid);
    }
  });

  projectData.EventHandlers?.forEach((eventHandler) => {
    eventHandler.Rules?.forEach((rule) => {
      rule.Actions?.forEach((action) => {
        if (
          action.Options?.find((opt) => opt.Value === arg?.UUID) ||
          action.TypeUUID === arg?.UUID ||
          action.BusinessHoursUUID === arg?.UUID ||
          action.Type === arg?.Type
        ) {
          handlers.push({
            eventHandler,
            rule,
            action,
          });
        }
      });
      rule.Conditions?.forEach((cond) => {
        if (
          cond.Options?.find((opt) => opt.Value === arg?.UUID) ||
          cond.TypeUUID === arg?.UUID ||
          cond.BusinessHoursUUID === arg?.UUID
        ) {
          handlers.push({
            eventHandler,
            rule,
            condition: cond,
          });
        }
      });
    });
  });
  handlers.forEach((handler) =>
    projectData.Workflows.forEach((workflow) => {
      workflow.EventListeners?.forEach((eventListener) => {
        if (handler.eventHandler.UUID === eventListener.EventHandlerUUID) {
          listeners.push({
            workflow,
            eventListener,
            ...handler,
          });
        }
      });
    })
  );
  // check workflows
  projectData.Workflows.forEach((workflow) => {
    if (sendLists.includes(workflow.UUID)) {
      listeners.push({
        workflow,
        sendList: true,
      });
    }
    if (workflow.MessageTemplateUUID === arg?.UUID) {
      listeners.push({
        workflow,
        isConfiguration: true,
      });
    }
    workflow.EventListeners?.forEach((eventListener) => {
      const listenerCondition = getListenerCondition(eventListener);

      if (arg.UUID === eventListener.EventHandlerUUID) {
        listeners.push({
          workflow,
          eventListener,
        });
      }
      if (
        listenerCondition?.Options?.find(
          (opt) => opt.Value === arg?.UUID
        ) ||
        listenerCondition?.TypeUUID === arg?.UUID ||
        listenerCondition?.BusinessHoursUUID === arg?.UUID
      ) {
        listeners.push({
          workflow,
          eventListener,
          eventHandler: projectData.EventHandlers?.find(
            (eventHandler) => eventHandler.UUID === eventListener.EventHandlerUUID
          ),
          condition: listenerCondition,
        });
      }
    });
  });
  // check the message templates where the entity is used
  projectData.MessageTemplates?.forEach((message) => {
    if (
      Object.values(message?.EditorState?.entityMap ?? {}).find(
        (entity) => entity.data.Value === arg?.UUID
      ) ||
      message.MessageConfigUUID === arg?.UUID ||
      (projectData.MessageConfigUUID === arg?.UUID &&
        !message.MessageConfigUUID) ||
      message.InputArgumentUUID === arg?.UUID
    ) {
      listeners.push({
        workflow: null,
        eventListener: null,
        eventHandler: null,
        messageTemplate: message,
      });
    }
    if (sendLists.includes(message.UUID)) {
      listeners.push({
        workflow: null,
        eventListener: null,
        eventHandler: null,
        messageTemplate: message,
        sendList: true,
      });
    }
  });

  return listeners;
};

export default usedIn;
